import React, { Component, Fragment } from 'react';
import { Container, Row, Col } from 'react-grid-system';

import { Loader } from 'components/loader';
import { InfoTooltip } from 'components/tooltip';
import { I18n, translate } from 'components/translate';
import { inject, observer, PropTypes as MobxPropTypes } from 'mobx-react';
import { DeepSearchService } from 'modules/userManager';
import { contentFilterFields } from 'modules/userManager/userSearch/deepSearch/constants';
import PropTypes from 'prop-types';

import DeepSearchContentService from './DeepSearchContentService';
import PagesContent from './PagesContent';
import QuizProfileContent from './QuizProfileContent';

import DeepSearchInputRange from '../commons/DeepSearchInputRange';
import DeepSearchRow from '../commons/DeepSearchRow';
import DeepSearchSelect from '../commons/DeepSearchSelect';

import './DeepSearchContent.scss';

const maxRange = {
  min: 0,
  max: 100
};

const clearFunctionKey = 'deepSearchContent';

const initialState = {
  userProgress: maxRange,
  userClass: {
    ids: [],
    open: false,
    value: ''
  },
  quizScore: {
    open: false,
    ids: [],
    min: 0,
    max: 100
  },
  quizProfile: {
    id: '',
    value: ''
  },
  profile: {
    value: '',
    label: ''
  },
  page: {
    open: false,
    completed: { ids: [], completed: true },
    incompleted: { ids: [], completed: false }
  },
  loaded: true
};

@translate()
@inject((stores) => ({
  contentFilter: stores.DeepSearchStore.deepSearch.contentFilter
}))
@observer
export default class DeepSearchContent extends Component {
  static propTypes = {
    contentFilter: MobxPropTypes.objectOrObservableObject,
    error: PropTypes.bool,
    setClearFunction: PropTypes.func,
    t: PropTypes.func,
    unsetClearFunction: PropTypes.func
  };

  state = {
    ...initialState,
    classesList: [],
    quizzesExerciseList: [],
    quizzesProfileList: [],
    profilesList: [],
    modulesTreeList: []
  };

  clearFunctions = [];

  clearState = () => {
    this.setState({ ...initialState });
    this.clearFunctions.forEach((clearFunction) => clearFunction());
  };

  setClearFunctionReference = (clearFunction) => {
    this.clearFunctions.push(clearFunction);
  };

  async componentDidMount() {
    const { userClassSelected, quizProfileSelected } =
      await this.loadListsData();
    const {
      userProgress,
      quizScore,
      page: completedPages = {},
      incompletePage = {}
    } = this.props.contentFilter;
    const { setClearFunction } = this.props;
    const completedIdsLen = completedPages.ids && completedPages.ids.length;
    const incompleteIdsLen = incompletePage.ids && incompletePage.ids.length;
    const hasCheckedPages = Boolean(completedIdsLen || incompleteIdsLen);

    setClearFunction && setClearFunction(clearFunctionKey, this.clearState);

    this.setState((prevState) => ({
      userProgress,
      userClass: {
        ...prevState.userClass,
        value: userClassSelected
      },
      quizProfile: {
        ...prevState.quizProfile,
        value: quizProfileSelected || ''
      },
      quizScore: {
        ...quizScore,
        open: Boolean(quizScore.ids.length)
      },
      page: {
        completed: completedPages,
        incompleted: incompletePage,
        open: hasCheckedPages
      }
    }));
  }

  componentWillUnmount() {
    const { unsetClearFunction } = this.props;

    unsetClearFunction && unsetClearFunction(clearFunctionKey);
  }

  async loadListsData() {
    this.toggleLoader();

    const {
      quizProfileSelected,
      classesList,
      quizzesExerciseList,
      quizzesProfileList,
      modulesTreeList,
      userClassSelected
    } = await DeepSearchContentService.getListsData();

    const { quizProfile } = this.props.contentFilter;

    if (quizProfileSelected && quizProfile.profileId) {
      await this.getProfiles(quizProfileSelected.id, quizProfile.profileId);
    }

    this.toggleLoader();

    this.setState({
      classesList,
      quizzesExerciseList,
      quizzesProfileList,
      modulesTreeList
    });

    return {
      userClassSelected,
      quizProfileSelected
    };
  }

  setInputRange(field, value) {
    const { min, max } = value;

    this.setState((prevState) => ({
      [field]: {
        ...prevState[field],
        min,
        max
      }
    }));

    DeepSearchContentService.updateStoreRangeValues(field, min, max);

    DeepSearchService.setDirty(true);
  }

  toggleLoader = () => {
    this.setState((prevState) => ({ loaded: !prevState.loaded }));
  };

  async getProfilesByQuiz(selectedOption) {
    this.toggleLoader();

    const id = selectedOption ? selectedOption.id : '';

    if (id) {
      await this.getProfiles(id);
    } else {
      const profile = {
        value: '',
        label: ''
      };

      this.setState({ profilesList: [], profile });
    }

    this.toggleLoader();
  }

  cleanSelectedProfile = () => this.setValue(null, contentFilterFields.profile);

  async setValue(selectedOption, field) {
    switch (field) {
      case contentFilterFields.userClass:
        const ids = DeepSearchContentService.getUserClassIds(selectedOption);

        DeepSearchContentService.updateStoreContentSelect(field, ids);
        break;
      case contentFilterFields.profile:
        const profileValue =
          DeepSearchContentService.getProfileValue(selectedOption);

        DeepSearchContentService.updateStoreProfile(
          contentFilterFields.quizProfile,
          profileValue
        );
        break;
      case contentFilterFields.quizProfile:
        DeepSearchContentService.updateStoreQuiz(field, selectedOption);
        await this.getProfilesByQuiz(selectedOption);
        this.cleanSelectedProfile();
        break;
      case contentFilterFields.quizScore:
        DeepSearchContentService.updateStoreQuizScore(
          contentFilterFields.quizScore,
          selectedOption
        );
        break;
      case contentFilterFields.page:
      case contentFilterFields.incompletePage:
        DeepSearchContentService.updateStorePage(field, selectedOption);
        break;
      default:
    }

    DeepSearchService.setDirty(true);

    this.setState((prevState) => ({
      [field]: {
        ...prevState[field],
        value: selectedOption
      }
    }));
  }

  async getProfiles(quizId, profileId = '') {
    const options = {
      quizId
    };
    let profile = this.state.profile;

    const profiles = await DeepSearchService.getQuizProfiles(options);
    const profilesList = profiles.map((profile) => ({
      value: profile.id,
      label: profile.name.replace('<p>', '').replace('</p>', '')
    }));

    if (profileId) {
      profile = profilesList.find((profile) => profile.value === profileId);
      profile.value = JSON.parse(JSON.stringify(profile));
    }

    this.setState({ profilesList, profile });
  }

  onMenuOpen(field, state) {
    const open = typeof state === 'undefined' ? !this.state[field].open : state;

    this.setState((prevState) => ({
      [field]: {
        ...prevState[field],
        open
      }
    }));
  }

  render() {
    const { error } = this.props;
    const { loaded } = this.state;

    return (
      <Fragment>
        <Container className="deep__search__content__container" fluid>
          <Row nogutter>
            <Col xs={12} md={3} className="deep__search__content__title__text">
              <I18n label="general.content" />
              <InfoTooltip
                rerenderPosition={true}
                text="users_manager.deep_search.access.content_tooltip"
              />
            </Col>
            <Col xs={12} md={9} />
          </Row>

          <DeepSearchRow label="general.progress">
            <DeepSearchInputRange
              formatLabel={(value) => `${value}%`}
              field={contentFilterFields.userProgress}
              min={this.state.userProgress.min}
              max={this.state.userProgress.max}
              setInputRange={this.setInputRange.bind(this)}
            />
          </DeepSearchRow>

          <DeepSearchRow label="users_import.manually.class.label">
            <DeepSearchSelect
              name="userClass"
              placeholder="users_import.manually.class.placeholder"
              field={contentFilterFields.userClass}
              options={this.state.classesList}
              value={this.state.userClass.value}
              onMenuOpen={this.onMenuOpen.bind(this)}
              isOpen={this.state.userClass.open}
              setValueSelect={this.setValue.bind(this)}
            />
          </DeepSearchRow>

          <DeepSearchRow label="users_manager.deep_search.content.page">
            <PagesContent
              name="page"
              field={contentFilterFields.page}
              incompleteField={contentFilterFields.incompletePage}
              items={this.state.modulesTreeList}
              fieldData={this.state.page}
              setClearFunction={this.setClearFunctionReference}
              onMenuOpen={this.onMenuOpen.bind(this)}
              setValue={this.setValue.bind(this)}
            />
          </DeepSearchRow>

          <DeepSearchRow label="users_manager.deep_search.content.quiz_score">
            <QuizProfileContent
              name="quizScore"
              field={contentFilterFields.quizScore}
              items={this.state.quizzesExerciseList}
              fieldData={this.state.quizScore}
              onMenuOpen={this.onMenuOpen.bind(this)}
              setValue={this.setValue.bind(this)}
              setClearFunction={this.setClearFunctionReference}
            />
          </DeepSearchRow>

          <DeepSearchRow label="users_manager.deep_search.content.quiz_profile">
            <DeepSearchSelect
              name="quizProfile"
              placeholder="users_manager.deep_search.content.quiz_profile_placeholder"
              field={contentFilterFields.quizProfile}
              options={this.state.quizzesProfileList}
              value={this.state.quizProfile.value}
              onMenuOpen={this.onMenuOpen.bind(this)}
              setValueSelect={this.setValue.bind(this)}
              isOpen={this.state.quizProfile.open}
            />
          </DeepSearchRow>

          {this.state.quizProfile.value && (
            <DeepSearchRow>
              <DeepSearchSelect
                name="profile"
                placeholder="users_manager.deep_search.content.profile_placeholder"
                field={contentFilterFields.profile}
                options={this.state.profilesList}
                value={this.state.profile.value}
                onMenuOpen={this.onMenuOpen.bind(this)}
                setValueSelect={this.setValue.bind(this)}
                isOpen={this.state.profile.open}
                error={error}
              />
            </DeepSearchRow>
          )}
        </Container>
        {!loaded && <Loader mask />}
      </Fragment>
    );
  }
}
